import React, { lazy, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useToast } from '~/_base/context/toastContext';
import { mutate } from 'swr';
import api from '~/_base/constants/api';
import { CurrentContext } from '~/models';
import Page from '~/_base/components/page';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
// @ts-ignore
import UserDialog from './modules/usersTable/userDialog';
import LocalesDialog from './modules/usersTable/localesDialog';
// @ts-ignore
import ProjectDialog from './modules/projectsTable/projectDialog';
// @ts-ignore
import ChangePasswordDialog from './modules/usersTable/changePasswordDialog';
const ProjectHome = lazy(() => import('./modules/projectHome'));
const ListsTable = lazy(() => import('~/frontend/modules/listsTable'));
const SchemaEditor = lazy(() =>
  import('~/frontend/modules/schemaEditor/schemaEditor')
);
const ListItemTable = lazy(() => import('~/frontend/modules/listItemTable'));
const AlertsTable = lazy(() => import('./modules/alertsTable'));
const MetricsTable = lazy(() => import('./modules/metricsTable'));
const TemplatesTable = lazy(() => import('./modules/templatesTable'));
const ProjectsTable = lazy(() =>
  import('./modules/projectsTable/projectsTable')
);
const UsersTable = lazy(() => import('./modules/usersTable/usersTable'));
const SiteAssistantLogTable = lazy(() =>
  import('./modules/siteAssistantLogTable')
);
const Bim360IntegrationWizard = lazy(() =>
  // @ts-ignore
  import('./modules/bim360integration/app')
);
// @ts-ignore
const AppMetrics = lazy(() => import('~/frontend/modules/metrics/appMetrics'));
const AppRegister = lazy(() =>
  // @ts-ignore
  import('~/frontend/modules/registers/containers/app')
);
const NotFound = lazy(() => import('./modules/notFound'));
const UnknownUser = lazy(() => import('./modules/unknownUser'));
const AppAlerts = lazy(() =>
  // @ts-ignore
  import('~/frontend/modules/alerts/appRulesEngine')
);
const ProjectMembersTable = lazy(() =>
  import('./modules/projectMembersTable/projectMembersTable')
);
const ProjectMemberAddEdit = lazy(() =>
  import('./modules/projectMembersTable/projectMemberAddEdit')
);

export function AuthenticatedRoutes(props: { currentContext: CurrentContext }) {
  const { currentContext } = props;
  const { errorToast } = useToast();
  let location = useLocation();
  let modal = location.state && location.state.background;
  const navigate = useNavigate();

  // This effect runs on every route change, including the initial request where
  // we receive HTML from the server. We use the `firstEffectRun` as a flag to
  // determine whether or not this was the first page load (meaning any
  // subsequent runs of the effect must be a result of client-side navigation)
  const firstEffectRun = useRef(true);
  const navigatingAwayFromModal = useRef(false);
  const row = document.getElementsByClassName('row') as HTMLCollectionOf<
    HTMLElement
  >;
  const rowExists = row.length > 0;
  const railsContentExists = rowExists && row[0].firstElementChild != null;
  useEffect(() => {
    if (modal != null) {
      // Inform the next run that we're navigating away from a modal
      navigatingAwayFromModal.current = true;
    } else if (
      (firstEffectRun.current && railsContentExists) ||
      (navigatingAwayFromModal.current && railsContentExists)
    ) {
      // This is the first run or we navigated away from a modal, so if rails
      // content exists we can safely reveal it
      row[0].style.display = 'block';
      firstEffectRun.current = false;
      navigatingAwayFromModal.current = false;
    } else {
      // Navigated to a client-side route
      const reactRoot = document.getElementById('ape_site_injection');
      if (rowExists && row[0] && reactRoot) {
        const appToUnmount = row[0].firstElementChild;
        appToUnmount && ReactDOM.unmountComponentAtNode(appToUnmount);
        const parentNode = row[0].parentNode;
        if (parentNode != null) parentNode.removeChild(row[0]);
        reactRoot.style.display = 'flex';
        reactRoot.style.flex = '1';
        reactRoot.style.width = '100%';
        for (var i = 0; i < document.styleSheets.length; i++) {
          var styleSheet = document.styleSheets[i];
          if (styleSheet.href) {
            styleSheet.disabled = true;
          }
        }
      }
    }
  }, [location, modal, railsContentExists, row, rowExists]);

  return (
    <React.Fragment>
      <Routes location={modal || location}>
        {/* Home */}
        <Route path="/" element={<Page title="Project Home"><ProjectHome /></Page>} />
        
        {/* Alerts */}
        <Route path="/alerts" element={<Page title="Alerts"><AlertsTable /></Page>} />
        <Route path="/alerts/new" element={<Page title="New Alert"><AppAlerts /></Page>} />
        <Route path="/alerts/:id" element={<Page title="Edit Alert"><AppAlerts /></Page>} />
        
        {/* Metrics */}
        <Route path="/metrics" element={<Page title="Metrics"><MetricsTable /></Page>} />
        <Route path="/metrics/new" element={<Page title="New Metric"><AppMetrics errorToast={errorToast} /></Page>} />
        <Route path="/metrics/:id" element={<Page title="Edit Metric"><AppMetrics errorToast={errorToast} /></Page>} />
        
        {/* Organisation Lists */}
        <Route path="/organisation_lists" element={<Page title="Organisation Lists"><ListsTable /></Page>} />
        <Route path="/organisation_lists/new" element={<Page title="New Organisation List"><SchemaEditor /></Page>} />
        <Route path="/organisation_lists/:list_id/edit" element={<Page title="Edit Organisation List"><SchemaEditor /></Page>} />
        <Route path="/organisation_lists/:list_id/items" element={<Page title="List Items"><ListItemTable /></Page>} />
        <Route path="/organisation_lists/:list_id/items/new" element={<Page title="New Item"><ListItemTable isCreate /></Page>} />
        <Route path="/organisation_lists/:list_id/items/:item_id/edit" element={<Page title="Edit Item"><ListItemTable /></Page>} />
        <Route path="/organisation_lists/:list_id/change_log" element={<Page title="Change Log"><ListItemTable showChangeLog /></Page>} />
        
        {/* Projects */}
        <Route path="/projects" element={<Page title="Projects"><ProjectsTable /></Page>} />
        <Route path="/projects/new" element={<Page title="New Project"><ProjectsTable isCreate /></Page>} />
        <Route path="/projects/:project_id/edit" element={<Page title="Edit Project"><ProjectsTable /></Page>} />
        <Route path="/projects/:project_id/project_members" element={<Page title="Project Members"><ProjectMembersTable /></Page>} />
        <Route path="/projects/:project_id/project_members/new" element={<Page title="New Project Member"><ProjectMemberAddEdit /></Page>} />
        <Route path="/projects/:project_id/project_members/:project_member_id/edit" element={<Page title="Edit Project Member"><ProjectMemberAddEdit /></Page>} />
        
        {/* Templates */}
        <Route path="/templates" element={<Page title="Templates"><TemplatesTable /></Page>} />
        
        {/* Users */}
        <Route path="/users" element={<Page title="Users"><UsersTable /></Page>} />
        <Route path="/users/new" element={<Page title="New User"><UsersTable isCreate /></Page>} />
        <Route path="/users/:id/edit" element={<Page title="Edit User"><UsersTable /></Page>} />
        <Route path="/users/:id/change_password" element={<Page title="Change Password"><UsersTable changePassword /></Page>} />
        
        {/* Site Assistant */}
        <Route path="/site_assistant/log" element={<Page title="Site Assistant Log"><SiteAssistantLogTable /></Page>} />
        
        {/* BIM360 */}
        <Route path="/bim360/external_systems/:id/activate" element={<Page title="BIM360 Integration"><Bim360IntegrationWizard mode="activate" /></Page>} />
        <Route path="/bim360/external_systems/:id/configure" element={<Page title="BIM360 Configuration"><Bim360IntegrationWizard mode="configure" /></Page>} />
        
        {/* Registers */}
        <Route path="/memos" element={<Page title="Memos"><AppRegister registerType="memos" /></Page>} />
        <Route path="/forms" element={<Page title="Forms"><AppRegister registerType="forms" /></Page>} />
        <Route path="/actions" element={<Page title="Actions"><AppRegister registerType="actions" /></Page>} />
        <Route path="/drawings" element={<Page title="Drawings"><AppRegister registerType="drawings" /></Page>} />
        <Route path="/reports" element={<Page title="Reports"><AppRegister registerType="punch_lists" /></Page>} />
        
        {/* Catch-all */}
        {!railsContentExists && <Route path="*" element={<Page title="Not Found"><NotFound /></Page>} />}
      </Routes>

      {/* Modal Routes */}
      {modal && (
        <Routes>
          {/* User Modals */}
          <Route path="/users/current/edit_profile" element={
            <UserDialog
              id={currentContext.current_user.id}
              open={true}
              onClose={() => navigate(-1)}
              onSave={() => navigate(-1)}
            />
          } />
          <Route path="/users/locales" element={
            <LocalesDialog
              id={currentContext.current_user.id}
              open={true}
              onClose={() => navigate(-1)}
              onSave={() => {
                navigate(-1);
                navigate('/');
              }}
            />
          } />
          <Route path="/users/current/reset_password" element={
            <ChangePasswordDialog
              open={true}
              id={currentContext.current_user.id}
              onClose={() => navigate(-1)}
              onSave={() => (window.location.href = '/signin')}
            />
          } />
          
          {/* Project Modals */}
          {currentContext.current_project != null && (
            <Route path="/projects/current/edit_project" element={
              <ProjectDialog
                id={currentContext.current_project?.id}
                open={true}
                onClose={() => navigate(-1)}
                onSave={() => {
                  navigate(-1);
                  mutate(api.projects.address);
                }}
              />
            } />
          )}
        </Routes>
      )}
    </React.Fragment>
  );
}

export function UnauthenticatedRoutes() {
  return (
    <Routes>
      <Route path="/unknown_user" element={<UnknownUser />} />
    </Routes>
  );
}
